import * as Context from "effect-app/Context"
import * as Effect from "effect-app/Effect"
import * as S from "effect-app/Schema"
import { NonNegativeNumber } from "effect-app/Schema"
import type * as Schema from "effect/Schema"
import * as SchemaTransformation from "effect/SchemaTransformation"
import { type HasTimestamps, TimestampProps } from "./Timestamps.ts"
import { removeVat, VatRate } from "./Vat.ts"

export const ShopId = S.StringId
export type ShopId = S.StringId

export class SwissMarket extends S.Opaque<SwissMarket, SwissMarket.Encoded>()(
  S
    .Struct({
      isActive: S.Boolean.withConstructorDefault,
      priceMultiplier: NonNegativeNumber.pipe(S.withConstructorDefault(Effect.succeed(NonNegativeNumber(1.5))))
    })
    .annotate({ identifier: "SwissMarket" })
) {}

export const ShopType = S.Literals(["Business", "Consumer"])
export type ShopType = typeof ShopType.Type

// relative path (`/...`) or absolute http(s) URL only — blocks `javascript:`/other schemes
// from being stored and later bound to an href (stored XSS).
const shippingCostsLinkPattern = /^(\/|https?:\/\/)/
export const ShippingCostsLink = S.NonEmptyString255.pipe(
  S.check(S.isPattern(shippingCostsLinkPattern, { identifier: "ShippingCostsLink" }))
)

export class Shop extends S.Opaque<Shop, Shop.Encoded>()(S.Struct({
  id: ShopId.withConstructorDefault,
  name: S.NonEmptyString255,
  magentoUrl: S.NonEmptyString255,
  magentoAccessToken: S.NonEmptyString255,
  isEnabled: S.Boolean,
  swissMarket: S.NullOr(SwissMarket),
  loggedinGroupIds: S.NullOr(S.NonEmptyArray(S.NonNegativeInt)).withConstructorDefault, // TODO: probably this should just be singular, though it wouldnt hurt anywy?
  type: ShopType, // for Consumer shop, we would always display the price.
  shippingCostsLink: S.NullOr(ShippingCostsLink), // relative or absolute URL; null = no link shown
  shippingCostsLinkNewTab: S.Boolean,
  vatRate: VatRate, // percent, applied only to Consumer shops; see models/Vat.ts
  ...TimestampProps
})) implements HasTimestamps {
  // Consumer shops display gross prices, but Magento treats all prices as net
  // and adds its own tax on top, so we hand it the net amount. Swiss shops are
  // exempt (historic behaviour: gross is passed through). Deliberately unrounded.
  static netPrice = (shop: Shop, price: number) =>
    shop.type === "Consumer" && !shop.swissMarket?.isActive
      ? removeVat(shop.vatRate)(price)
      : price

  static readonly resolver = Context.Service<ShopFromId, (userId: ShopId) => Effect.Effect<Shop>>(
    "ShopFromId"
  )
}

export interface ShopFromId {
  readonly _: unique symbol
}

export const ShopFromId: Schema.Codec<Shop, string, ShopFromId> = ShopId
  .pipe(S.decodeTo(
    S.toType(Shop),
    SchemaTransformation.transformEffect({
      decode: (id) => Shop.resolver.use((resolve) => resolve(id)),
      encode: (shop) => Effect.succeed(shop.id)
    })
  ))

// codegen:start {preset: model}
//
export namespace SwissMarket {
  export interface Encoded extends S.StructNestedEncoded<typeof SwissMarket> {}
}
export namespace Shop {
  export interface Encoded extends S.StructNestedEncoded<typeof Shop> {}
}
//
// codegen:end
//
